/**
 * Layout autenticado del portal de clientes.
 *
 * Brand principal Axorial; si la company del cliente tiene logo + accent_color,
 * los mostramos sutilmente en el header (logo a la izquierda, tinte en el
 * borde sticky). El resto del look es Axorial.
 *
 * Nav drawer DaisyUI (siempre visible en lg, hamburger debajo).
 */
import { Link, Outlet, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  LayoutDashboard,
  FolderKanban,
  Ticket,
  FileText,
  HardDrive,
  LogOut,
  Menu,
} from "lucide-react";
import { useState } from "react";
import { api } from "@/lib/api";
import { useMe } from "@/lib/session";

/**
 * Items de nav con su permiso requerido. Dashboard y Proyectos no
 * necesitan permiso (proyectos.view es implícito para todo cliente
 * activo). Las demás pestañas se ocultan si el cliente no tiene el
 * permiso correspondiente.
 */
const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, perm: null },
  { to: "/projects", label: "Proyectos", icon: FolderKanban, perm: null },
  { to: "/tickets", label: "Tickets", icon: Ticket, perm: "tickets.view" },
  { to: "/invoices", label: "Facturas", icon: FileText, perm: "invoices.view" },
  { to: "/drive", label: "Archivos", icon: HardDrive, perm: "archives.view" },
] as const;

export function AppShell() {
  const { data: me } = useMe();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const logout = useMutation({
    mutationFn: () => api.post("/auth/logout", {}),
    onSettled: () => {
      qc.clear();
      navigate({ to: "/login" });
    },
  });

  // Branding sutil: tomamos el primer company y su accent_color para
  // tintar borde del sidebar. Si no hay accent, queda Axorial neutro.
  const primaryCompany = me?.companies?.[0];
  const accent = primaryCompany?.accentColor;

  return (
    <div className="drawer lg:drawer-open">
      <input
        id="cli-drawer"
        type="checkbox"
        className="drawer-toggle"
        checked={drawerOpen}
        onChange={(e) => setDrawerOpen(e.target.checked)}
      />
      <div className="drawer-content flex flex-col min-h-screen bg-base-200">
        <header className="min-h-14 px-4 md:px-6 py-2 border-b border-base-300 glass-panel flex items-center justify-between gap-3 sticky top-0 z-10">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <label
              htmlFor="cli-drawer"
              className="btn btn-ghost btn-sm btn-square lg:hidden -ml-2 shrink-0"
              aria-label="Abrir menú"
            >
              <Menu className="size-5" />
            </label>
            <div className="font-display font-semibold text-base-content tracking-tight">
              Axorial · Portal
            </div>
            {primaryCompany && (
              <span className="badge badge-sm gap-2" style={accent ? { borderColor: accent } : undefined}>
                {primaryCompany.logoR2Key && (
                  <img
                    src={`/api/companies/${primaryCompany.id}/logo`}
                    alt={primaryCompany.name}
                    className="size-4 object-contain"
                  />
                )}
                {primaryCompany.name}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs sm:text-sm opacity-60 hidden sm:block">
              {me?.user?.email}
            </span>
            <button
              type="button"
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
              className="btn btn-ghost btn-sm"
              title="Cerrar sesión"
            >
              <LogOut className="size-4" />
              <span className="hidden md:inline">Salir</span>
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
          <div className="mx-auto w-full 2xl:max-w-[1400px]">
            <Outlet />
          </div>
        </div>
      </div>

      <div className="drawer-side z-20">
        <label htmlFor="cli-drawer" aria-label="Cerrar menú" className="drawer-overlay" />
        <aside
          className="min-h-full sidebar-gradient text-white/90 flex flex-col w-64"
          style={accent ? { borderRight: `3px solid ${accent}` } : undefined}
        >
          <div className="px-5 py-5 border-b border-white/10">
            <img
              src="/logo/Logo-Axorial-Calado-Blanco.png"
              alt="Axorial"
              className="h-7 w-auto mb-1.5"
            />
            <div className="text-xs text-white/55">Portal de Clientes</div>
          </div>
          <nav className="flex-1 p-3 space-y-1">
            {NAV.filter(
              (n) => !n.perm || (me?.user.permissions ?? []).includes(n.perm),
            ).map((n) => {
              const Icon = n.icon;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setDrawerOpen(false)}
                  className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium hover:bg-white/10 text-white/80 hover:text-white transition-colors"
                  activeProps={{ className: "bg-white/15 text-white" }}
                >
                  <Icon className="size-4" />
                  {n.label}
                </Link>
              );
            })}
          </nav>
          {me?.user && (
            <div className="px-5 py-4 border-t border-white/10 text-xs text-white/70">
              <div className="font-medium text-white">{me.user.name}</div>
              <div className="truncate">{me.user.email}</div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
